// @flow
import _ from 'lodash'
import type { Store } from '../ducks'
import segmentAudience from '../../components/AudienceEdit/segmentAudience'
import collectAudienceName from '../ducks/audienceEdit/collectAudienceName'
import type { State as AudienceEditState } from '../ducks/audienceEdit/reducers'
import memoizeOne from 'memoize-one'
import type { AudienceDataForSegmentation } from '../../components/types'

const maxSegmentsCount = 100

export type SegmentationResult = {|
  maxSegmentsCount: number,
  isSegmenting: boolean,
  isLimitExceeded: boolean,
  count: number,
  items: Array<AudienceEditState>
|}

export const getAudienceEditSegments = memoizeOne(function(state: Store): SegmentationResult {
  const audienceEdit = state.audienceEdit
  const segmentation = state.segmentation
  const isSegmenting = _.some(_.values(segmentation))

  let result: SegmentationResult = {
    maxSegmentsCount,
    isSegmenting,
    isLimitExceeded: false,
    count: 1,
    items: []
  }
  if (!isSegmenting) {
    result.items = [audienceEdit]
    return result
  }

  const segmentResult = segmentAudience(pickDataToSegment(audienceEdit), segmentation)
  result.count = segmentResult.count
  result.isLimitExceeded = segmentResult.count > maxSegmentsCount

  if (result.isLimitExceeded) {
    result.items = []
    return result
  }

  const segments = segmentResult.dataFunc()
  result.items = _.sortBy(
    _.map(segments, (segment) => {
      const patched = { ...audienceEdit, ...segment }
      return { ...patched, name: collectAudienceName(patched, segmentation) }
    }),
    (item) => item.name
  )

  return result
})

function pickDataToSegment(audienceState: AudienceEditState): AudienceDataForSegmentation {
  return {
    minAge: audienceState.minAge,
    maxAge: audienceState.maxAge,
    fbPlacements: audienceState.fbPlacements,
    sex: audienceState.sex,
    includedLocationGroups: audienceState.includedLocationGroups,
    excludedLocationIds: audienceState.excludedLocationIds,
    locationsWithChildren: audienceState.locationsWithChildren,
    providers: audienceState.providers,
    includedSegmentIds: audienceState.includedSegmentIds,
    includedContactIds: audienceState.includedContactIds,
    includedLookalikeSegmentIds: audienceState.includedLookalikeSegmentIds,
    includedLookalikeContactIds: audienceState.includedLookalikeContactIds,
    includedProviderTargetingSourceIds: audienceState.includedProviderTargetingSourceIds,
    segments: audienceState.segments,
    contacts: audienceState.contacts,
    lookalikeSegments: audienceState.lookalikeSegments,
    lookalikeContacts: audienceState.lookalikeContacts,
    providerTargetingSources: audienceState.providerTargetingSources,
    includedVkAppsGroups: audienceState.includedVkAppsGroups,
    includedGroups: audienceState.includedGroups,
    keywordsGroups: audienceState.keywordsGroups,
    vkActivityGroups: audienceState.vkActivityGroups,
    interestIds: audienceState.interestIds,
    providerInterests: audienceState.providerInterests,
    platform: audienceState.platform,
    localGeo: audienceState.localGeo,
    fbAdInterests: audienceState.fbAdInterests,
    placementTypes: audienceState.placementTypes
  }
}
